import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { registerCustomer } from '../services/customersService';
import { EMAIL_REGEX, PHONE_REGEX, normalizeInstagram } from '../utils/validation';
import PaymentProofUploadField from '../components/PaymentProofUploadField';
import { supabase } from '../lib/supabase';
import { useLocale } from '../hooks/useLocale';
import { formatPrice as fmtPrice, formatDate as fmtDate } from '../utils/format';
import { tField } from '../utils/i18nContent';
import PasswordInput from '../components/PasswordInput';
import { clarity } from '../lib/clarity';
import MailingAccountCard from '../components/MailingAccountCard';
import MercadoPagoLinkButton from '../components/MercadoPagoLinkButton';
import { allyOrderPaymentMethod } from '../services/allyOrderService';
import { artistOrderPaymentMethod } from '../services/artistOrderService';
import PaymentLinkButton from '../components/PaymentLinkButton';

// Supabase Auth caps confirmation/reset emails per hour for the whole project;
// over the cap it rejects the request with 429 'over_email_send_rate_limit'.
const isEmailRateLimited = (err) =>
  err?.code === 'over_email_send_rate_limit' || err?.status === 429;

// Records which Supabase Auth error a failed signup/reset hit (e.g. the hourly
// email cap vs. the per-IP request limit), since the UI shows one message for
// every 429. Goes to Clarity as a session tag + event; no email or PII.
const logAuthError = (flow, err) => {
  const code = err?.code || (err?.status ? `http_${err.status}` : 'unknown');
  console.warn(`[auth] ${flow} failed`, { code, status: err?.status ?? null });
  clarity.setTag(`${flow}_error_code`, code);
  clarity.event(`${flow}_failed_${code}`);
};

const inputStyle = {
  width: '100%',
  padding: '0.75rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '1rem'
};

// Labels resolved at render via t(`tracking.${stage.key}`) so they swap with the locale.
const TRACKING_STAGES = [
  { key: 'confirmed' },
  { key: 'en_preparacion' },
  { key: 'enviado' },
  { key: 'entregado' }
];

// Pickup orders ("Recolección") take the "Enviado" slot in the strip.
const getTrackingStages = (order) =>
  order.fulfillmentStatus === 'recoleccion'
    ? TRACKING_STAGES.map((stage) => (stage.key === 'enviado' ? { key: 'recoleccion' } : stage))
    : TRACKING_STAGES;

const getTrackingStepIndex = (order) => {
  const f = order.fulfillmentStatus || 'confirmed';
  if (f === 'entregado') return 3;
  if (f === 'enviado' || f === 'recoleccion') return 2;
  if (f === 'en_preparacion') return 1;
  return 0;
};

const AccountPage = () => {
  const {
    user,
    session,
    loading: authLoading,
    isRecoveringPassword,
    signInWithPassword,
    signUp,
    signOut,
    resetPasswordForEmail,
    updatePassword
  } = useAuth();
  const locale = useLocale();
  const { t } = useTranslation('account');
  const formatPrice = (price, currency = 'MXN') => fmtPrice(price, currency, locale);
  const [view, setView] = useState('login'); // 'login' | 'register' | 'forgot' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [instagram, setInstagram] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const [paymentModalOrder, setPaymentModalOrder] = useState(null);

  const refreshOrders = useCallback(async () => {
    if (!user || !session?.access_token) return;
    try {
      const res = await fetch('/api/my-orders', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setOrders(data.orders || []);
    } catch (_) {
      /* silent refresh */
    }
  }, [user, session?.access_token]);

  // When the user lands here from a password recovery email, force the
  // "set new password" view regardless of the otherwise-active session.
  useEffect(() => {
    if (isRecoveringPassword) {
      setView('reset');
      setPassword('');
      setConfirmPassword('');
      setMessage(null);
    }
  }, [isRecoveringPassword]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage(null);
    if (!email.trim()) {
      setMessage(t('login.errors.emailRequired'));
      return;
    }
    if (!password) {
      setMessage(t('login.errors.passwordRequired'));
      return;
    }
    setLoading(true);
    try {
      await signInWithPassword({ email: email.trim(), password });
      setMessage(null);
      clarity.event('login_completed');
    } catch (err) {
      const msg = err.message || t('login.errors.unknown');
      setMessage(msg.includes('Invalid login') ? t('login.errors.invalidCredentials') : msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setMessage(null);
    if (!name.trim()) {
      setMessage(t('register.errors.nameRequired'));
      return;
    }
    if (!email.trim()) {
      setMessage(t('register.errors.emailRequired'));
      return;
    }
    if (!phone.trim()) {
      setMessage(t('register.errors.phoneRequired'));
      return;
    }
    if (!PHONE_REGEX.test(phone.trim())) {
      setMessage(t('register.errors.phoneInvalid'));
      return;
    }
    if (!EMAIL_REGEX.test(email.trim())) {
      setMessage(t('register.errors.emailInvalid'));
      return;
    }
    // Optional; null = left empty, undefined = not a valid handle
    const instagramHandle = normalizeInstagram(instagram);
    if (instagramHandle === undefined) {
      setMessage(t('register.errors.instagramInvalid'));
      return;
    }
    if (!password) {
      setMessage(t('register.errors.passwordRequired'));
      return;
    }
    if (password.length < 6) {
      setMessage(t('register.errors.passwordShort'));
      return;
    }
    if (password !== confirmPassword) {
      setMessage(t('register.errors.passwordMismatch'));
      return;
    }
    setLoading(true);
    try {
      const { user: u } = await signUp({ email: email.trim(), password, name: name.trim(), phone: phone.trim(), instagram: instagramHandle, marketingOptIn });
      if (u?.identities?.length === 0) {
        setMessage(t('register.errors.checkEmail'));
      } else {
        try {
          await registerCustomer({ email: email.trim(), name: name.trim(), phone: phone.trim(), instagram: instagramHandle });
        } catch (_) {
          // Non-blocking: customer row may already exist or API unavailable
        }
        setMessage(t('register.success'));
        setRegisterSuccess(true);
        clarity.event('signup_completed');
      }
    } catch (err) {
      logAuthError('signup', err);
      const msg = err.message || t('register.errors.unknown');
      if (isEmailRateLimited(err)) setMessage(t('register.errors.rateLimited'));
      else setMessage(msg.includes('already registered') ? t('register.errors.alreadyRegistered') : msg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setMessage(null);
    if (!email.trim()) {
      setMessage(t('forgot.errors.emailRequired'));
      return;
    }
    if (!EMAIL_REGEX.test(email.trim())) {
      setMessage(t('forgot.errors.emailInvalid'));
      return;
    }
    setLoading(true);
    try {
      await resetPasswordForEmail({ email: email.trim() });
      setMessage(t('forgot.success'));
      setForgotSuccess(true);
    } catch (err) {
      logAuthError('password_reset', err);
      setMessage(isEmailRateLimited(err) ? t('forgot.errors.rateLimited') : (err.message || t('forgot.errors.unknown')));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMessage(null);
    if (!password) {
      setMessage(t('reset.errors.passwordRequired'));
      return;
    }
    if (password.length < 6) {
      setMessage(t('reset.errors.passwordShort'));
      return;
    }
    if (password !== confirmPassword) {
      setMessage(t('reset.errors.passwordMismatch'));
      return;
    }
    setLoading(true);
    try {
      await updatePassword({ password });
      // Sign out the recovery session so the user logs back in with the new password
      try { await signOut(); } catch (_) {}
      setPassword('');
      setConfirmPassword('');
      setView('login');
      setMessage(t('reset.success'));
    } catch (err) {
      setMessage(err.message || t('reset.errors.unknown'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    setMessage(null);
    try {
      await signOut();
    } catch (err) {
      setMessage(err.message || t('signOutError'));
    }
  };

  // Fetch orders when user is logged in
  useEffect(() => {
    if (!user || !session?.access_token) {
      setOrders([]);
      setOrdersError(null);
      return;
    }
    let cancelled = false;
    setOrdersLoading(true);
    setOrdersError(null);
    fetch('/api/my-orders', {
      headers: { Authorization: `Bearer ${session.access_token}` }
    })
      .then(res => {
        if (res.status === 401) {
          // Token is expired or revoked. Clear the dead session so the UI
          // doesn't stay stuck on a "logged in but broken" state, and surface
          // an explanation on the sign-in form the user will see next.
          setMessage(t('orders.sessionExpired'));
          signOut().catch(() => {});
          throw new Error(t('orders.sessionExpired'));
        }
        if (res.status === 404) {
          return { orders: [] };
        }
        if (!res.ok) {
          throw new Error(t('orders.loadError'));
        }
        return res.json();
      })
      .then(data => {
        if (!cancelled) {
          setOrders(data.orders || []);
          setOrdersError(null);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setOrdersError(err.message || t('orders.loadError'));
          setOrders([]);
        }
      })
      .finally(() => {
        if (!cancelled) setOrdersLoading(false);
      });
    return () => { cancelled = true; };
  }, [user, session?.access_token, signOut, t]);

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || t('userFallback');

  if (authLoading) {
    return (
      <div className="container" style={{ padding: '2rem 0', textAlign: 'center' }}>
        <p style={{ color: '#666' }}>{t('loading')}</p>
      </div>
    );
  }

  if (user && !isRecoveringPassword) {
    return (
      <div className="container" style={{ padding: '2rem 0', width: '100%' }}>
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
          width: '100%'
        }}>
          <h1 style={{
            marginBottom: '1.5rem',
            fontSize: '1.75rem',
            color: '#333',
            fontFamily: "'Gambado Sans', sans-serif"
          }}>
            {t('title')}
          </h1>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            <Trans
              i18nKey="account:greeting"
              values={{ name: displayName }}
              components={{ strong: <strong /> }}
            />
          </p>
          <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '1.5rem' }}>
            {user.email}
          </p>

          {user?.user_metadata?.wants_artist_profile && (
            <Link
              to="/account/apply-artist"
              style={{
                display: 'inline-block',
                padding: '0.55rem 1.25rem',
                background: 'none',
                border: '1px solid #1a1a1a',
                borderRadius: '999px',
                color: '#1a1a1a',
                textDecoration: 'none',
                fontSize: '0.9rem',
                fontFamily: "'Gambado Sans', sans-serif",
                letterSpacing: '0.04em',
                marginBottom: '1.5rem',
              }}
            >
              {t('applyArtist')}
            </Link>
          )}

          {message && (
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>{message}</p>
          )}

          <MailingAccountCard user={user} accessToken={session?.access_token} />

          {/* Mis pedidos */}
          <h2 style={{
            marginTop: '2rem',
            marginBottom: '1rem',
            fontSize: '1.25rem',
            color: '#333',
            fontFamily: "'Gambado Sans', sans-serif"
          }}>
            {t('orders.heading')}
          </h2>
          {ordersLoading && (
            <p style={{ color: '#666', fontSize: '0.95rem' }}>{t('orders.loading')}</p>
          )}
          {ordersError && !ordersLoading && (
            <p style={{ color: '#dc3545', fontSize: '0.95rem' }}>{ordersError}</p>
          )}
          {!ordersLoading && !ordersError && orders.length === 0 && (
            <p style={{ color: '#666', fontSize: '0.95rem' }}>{t('orders.empty')}</p>
          )}
          {!ordersLoading && !ordersError && orders.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {orders.map(order => {
                const items = Array.isArray(order.items) ? order.items : [];
                const ref = (order.paymentIntentId || order.id || '').toString().slice(-8);
                const orderKey = order.id || order.paymentIntentId;
                const dateStr = order.createdAt
                  ? fmtDate(order.createdAt, locale)
                  : '';
                const hasAddress = !!(order.customerAddress || order.customerCity);
                return (
                  <div
                    key={orderKey}
                    style={{
                      padding: '1rem',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #eee',
                      margin: '0.5rem 0'
                    }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontWeight: '600', color: '#333' }}>{t('orders.orderNumber', { ref })}</span>
                        <span style={{ color: '#666', fontSize: '0.9rem', marginLeft: '0.5rem' }}>{dateStr}</span>
                        {order.source === 'manual' && (
                          <span style={{ display: 'inline-block', marginLeft: '0.5rem', padding: '1px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: '600', background: '#eaf7ed', color: '#28a745' }}>
                            {t('orders.manualSale')}
                          </span>
                        )}
                        <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#333' }}>
                          {items.map((item, idx) => {
                            const displayName = tField(item, 'name', locale) || item.name;
                            return (
                            <div key={item.id != null ? `${orderKey}-${item.id}` : idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                              <img
                                src={item.variantImage || item.image || 'https://via.placeholder.com/40x40/f0f0f0/999?text=+'}
                                alt={displayName || t('orders.itemImageAlt')}
                                loading="lazy"
                                style={{
                                  width: 40,
                                  height: 40,
                                  objectFit: 'cover',
                                  borderRadius: 6,
                                  border: '1px solid #eee',
                                  flexShrink: 0
                                }}
                                onError={(e) => { e.target.src = 'https://via.placeholder.com/40x40/f0f0f0/999?text=+'; }}
                              />
                              <span style={{ flex: 1, minWidth: 0 }}>
                                {item.isPreorder
                                  ? t('orders.itemDeposit', { name: displayName })
                                  : (displayName || t('orders.itemFallback'))}
                                {item.variantName && (
                                  <span style={{ color: '#888' }}> — {item.variantName}</span>
                                )}
                                {t('orders.itemRow', {
                                  count: item.quantity ?? 1,
                                  price: formatPrice((item.price || 0) * (item.quantity || 1), order.currency),
                                })}
                              </span>
                            </div>
                            );
                          })}
                        </div>
                        {hasAddress && (
                          <>
                            <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem' }}>{t('orders.shippingAddress')}</h4>
                            <p style={{ margin: 0, lineHeight: 1.5 }}>
                              {order.customerAddress && <>{order.customerAddress}<br /></>}
                              {[order.customerCity, order.customerState, order.customerZipCode].filter(Boolean).join(', ')}
                              {order.customerCountry && <><br />{order.customerCountry}</>}
                            </p>
                          </>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: 0, fontWeight: '600', color: '#E4405F' }}>
                          {formatPrice(order.totalAmount, order.currency)}
                        </p>
                      </div>
                    </div>
                    {order.allowsPartialPayment && (() => {
                      const total = Number(order.totalAmount || 0);
                      const paid = Number(order.amountPaid || 0);
                      const remaining = Math.max(0, total - paid);
                      const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
                      const fullyPaid = order.paymentStatus === 'paid' || remaining <= 0.01;
                      return (
                        <div style={{ marginTop: '1rem', padding: '0.85rem', background: '#fff', border: '1px solid #eee', borderRadius: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'baseline', marginBottom: '0.5rem' }}>
                            <strong style={{ fontSize: '0.95rem' }}>Pago parcial</strong>
                            <span style={{ fontSize: '0.85rem', color: '#666' }}>
                              {formatPrice(paid, order.currency)} de {formatPrice(total, order.currency)}
                            </span>
                          </div>
                          <div style={{ width: '100%', height: '8px', background: '#eee', borderRadius: '999px', overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: fullyPaid ? '#28a745' : '#2ca9e1', transition: 'width .25s ease' }} />
                          </div>
                          {!fullyPaid && (
                            <>
                              <p style={{ margin: '0.5rem 0 0.75rem', fontSize: '0.85rem', color: '#666' }}>
                                Te faltan <strong>{formatPrice(remaining, order.currency)}</strong> por pagar.
                              </p>
                              {/* Ally orders are paid to the ally (its Mercado Pago links or
                                  bank account), never through Bluppimart's payment modal. */}
                              {allyOrderPaymentMethod(order.paymentIntentId) === 'mercado_pago_link' ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                  {order.items.filter((item) => item.mercado_pago_url).map((item) => (
                                    <MercadoPagoLinkButton key={item.id} href={item.mercado_pago_url}>
                                      Pagar {item.name} con Mercado Pago
                                    </MercadoPagoLinkButton>
                                  ))}
                                </div>
                              ) : artistOrderPaymentMethod(order.paymentIntentId) === 'payment_link' ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                  {order.items.filter((item) => item.payment_link_url).map((item) => (
                                    <PaymentLinkButton key={`${item.id}-${item.variantId || ''}`} href={item.payment_link_url} provider={item.payment_link_provider}>
                                      Pagar {item.name} con {item.payment_link_provider === 'clip' ? 'Clip' : 'Mercado Pago'}
                                    </PaymentLinkButton>
                                  ))}
                                </div>
                              ) : artistOrderPaymentMethod(order.paymentIntentId) === 'bank_transfer' ? (
                                <p style={{ margin: 0, fontSize: '0.85rem', color: '#555' }}>
                                  Transfiere directo al artista con los datos que te enviamos por correo, usando tu usuario de Instagram como concepto.
                                </p>
                              ) : allyOrderPaymentMethod(order.paymentIntentId) === 'bank_transfer' ? (
                                <p style={{ margin: 0, fontSize: '0.85rem', color: '#555' }}>
                                  Transfiere directo al aliado con los datos que te enviamos por correo, usando tu usuario de Instagram como concepto.
                                </p>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setPaymentModalOrder(order)}
                                  style={{
                                    padding: '0.55rem 1.1rem',
                                    background: '#2ca9e1',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                  }}
                                >
                                  Realizar pago
                                </button>
                              )}
                            </>
                          )}
                          {fullyPaid && (
                            <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: '#28a745', fontWeight: '600' }}>
                              Pagado en su totalidad
                            </p>
                          )}
                        </div>
                      );
                    })()}
                    {/* Delivery tracking strip hides while the order is still
                        awaiting payment — the "Pago parcial" card above is the
                        relevant status; lighting up "Confirmado" here would
                        misrepresent where the order actually is in the flow. */}
                    {order.fulfillmentStatus !== 'awaiting_payment' && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
                      <div style={{ width: '100%', maxWidth: '320px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                          {getTrackingStages(order).map((stage, idx, stages) => {
                              const currentStep = getTrackingStepIndex(order);
                              const reached = idx <= currentStep;
                              const isLast = idx === stages.length - 1;
                              return (
                                <React.Fragment key={stage.key}>
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: isLast ? 'none' : 1 }}>
                                    <div style={{
                                      width: '10px',
                                      height: '10px',
                                      borderRadius: '50%',
                                      background: reached ? '#2ca9e1' : '#e0e0e0',
                                      border: `2px solid ${reached ? '#2ca9e1' : '#e0e0e0'}`,
                                      flexShrink: 0
                                    }} />
                                    <span style={{
                                      fontSize: '0.65rem',
                                      marginTop: '0.25rem',
                                      color: reached ? '#2ca9e1' : '#999',
                                      fontWeight: reached ? '600' : '400',
                                      textAlign: 'center',
                                      lineHeight: 1.2
                                    }}>
                                      {t(`tracking.${stage.key}`)}
                                    </span>
                                  </div>
                                  {!isLast && (
                                    <div style={{
                                      flex: 1,
                                      height: '2px',
                                      marginTop: '4px',
                                      marginLeft: '2px',
                                      marginRight: '2px',
                                      background: idx < currentStep ? '#2ca9e1' : '#e0e0e0',
                                      alignSelf: 'flex-start',
                                      minWidth: '8px'
                                    }} />
                                  )}
                                </React.Fragment>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <button
            type="button"
            onClick={handleSignOut}
            style={{
              width: '100%',
              marginTop: '2rem',
              padding: '0.75rem 1rem',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {t('signOut')}
          </button>
        </div>
        {paymentModalOrder && (
          <PaymentModal
            order={paymentModalOrder}
            user={user}
            session={session}
            onClose={() => setPaymentModalOrder(null)}
            onSuccess={async () => {
              setPaymentModalOrder(null);
              await refreshOrders();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '2rem 0', width: '100%' }}>
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        width: '100%'
      }}>
        <h1 style={{
          marginBottom: '1.5rem',
          fontSize: '1.75rem',
          color: '#333',
          fontFamily: "'Gambado Sans', sans-serif"
        }}>
          {t('title')}
        </h1>

        {view === 'login' && (
          <>
            <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
              {t('login.intro')}
            </p>
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: '500', color: '#333' }}>
                  {t('login.email')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('login.emailPlaceholder')}
                  className="form-input"
                  style={inputStyle}
                  autoComplete="email"
                />
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: '500', color: '#333' }}>
                  {t('login.password')}
                </label>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('login.passwordPlaceholder')}
                  className="form-input"
                  style={inputStyle}
                  autoComplete="current-password"
                />
              </div>
              <div style={{ textAlign: 'right', marginBottom: '1.25rem' }}>
                <button
                  type="button"
                  onClick={() => { setView('forgot'); setMessage(null); setPassword(''); setForgotSuccess(false); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2ca9e1',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '0.9rem',
                    textDecoration: 'underline'
                  }}
                >
                  {t('login.forgot')}
                </button>
              </div>
              {message && (
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>{message}</p>
              )}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: '#2ca9e1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: loading ? 'wait' : 'pointer'
                }}
              >
                {loading ? t('login.submitting') : t('login.submit')}
              </button>
            </form>
            <p style={{ marginTop: '1.25rem', fontSize: '0.9rem', color: '#666' }}>
              {t('login.noAccount')}{' '}
              <button
                type="button"
                onClick={() => setView('register')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2ca9e1',
                  cursor: 'pointer',
                  padding: 0,
                  textDecoration: 'underline'
                }}
              >
                {t('login.registerCta')}
              </button>
            </p>
          </>
        )}

        {view === 'register' && (
          <>
            {registerSuccess ? (
              <>
                <p style={{ color: '#333', marginBottom: '1.5rem', fontSize: '1rem', lineHeight: 1.6 }}>
                  {message}
                </p>
                <button
                  type="button"
                  onClick={() => { setView('login'); setRegisterSuccess(false); setMessage(null); }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: '#2ca9e1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {t('register.successSignIn')}
                </button>
              </>
            ) : (
              <>
            <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
              {t('register.intro')}
            </p>
            <form onSubmit={handleRegister}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: '500', color: '#333' }}>
                  {t('register.name')} <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('register.namePlaceholder')}
                  className="form-input"
                  style={inputStyle}
                  autoComplete="name"
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: '500', color: '#333' }}>
                  {t('register.email')} <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('register.emailPlaceholder')}
                  className="form-input"
                  style={inputStyle}
                  autoComplete="email"
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: '500', color: '#333' }}>
                  {t('register.phone')} <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t('register.phonePlaceholder')}
                  className="form-input"
                  style={inputStyle}
                  autoComplete="tel"
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: '500', color: '#333' }}>
                  {t('register.instagram')}
                </label>
                <input
                  type="text"
                  value={instagram}
                  onChange={(e) => setInstagram(e.target.value)}
                  placeholder={t('register.instagramPlaceholder')}
                  className="form-input"
                  style={inputStyle}
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: '500', color: '#333' }}>
                  {t('register.password')} <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('register.passwordPlaceholder')}
                  className="form-input"
                  style={inputStyle}
                  autoComplete="new-password"
                />
              </div>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: '500', color: '#333' }}>
                  {t('register.confirmPassword')} <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <PasswordInput
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('register.confirmPasswordPlaceholder')}
                  className="form-input"
                  style={inputStyle}
                  autoComplete="new-password"
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', marginBottom: '1rem', cursor: 'pointer', fontSize: '0.9rem', color: '#333', lineHeight: 1.5 }}>
                <input
                  type="checkbox"
                  checked={marketingOptIn}
                  onChange={(e) => setMarketingOptIn(e.target.checked)}
                  style={{ width: 16, height: 16, marginTop: 3, cursor: 'pointer', flexShrink: 0 }}
                />
                <span>{t('register.marketingOptIn')}</span>
              </label>
              <p style={{ fontSize: '0.85rem', color: '#666', lineHeight: 1.5, marginBottom: '1.25rem' }}>
                <Trans
                  i18nKey="register.privacyNotice"
                  t={t}
                  components={{
                    termsLink: <Link to="/terminos-y-condiciones" target="_blank" rel="noopener noreferrer" />,
                    privacyLink: <Link to="/terminos-y-condiciones#aviso-de-privacidad" target="_blank" rel="noopener noreferrer" />
                  }}
                />
              </p>
              {message && (
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>{message}</p>
              )}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: '#2ca9e1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: loading ? 'wait' : 'pointer'
                }}
              >
                {loading ? t('register.submitting') : t('register.submit')}
              </button>
            </form>
            <p style={{ marginTop: '1.25rem', fontSize: '0.9rem', color: '#666' }}>
              {t('register.hasAccount')}{' '}
              <button
                type="button"
                onClick={() => setView('login')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2ca9e1',
                  cursor: 'pointer',
                  padding: 0,
                  textDecoration: 'underline'
                }}
              >
                {t('register.loginCta')}
              </button>
            </p>
              </>
            )}
          </>
        )}

        {view === 'forgot' && (
          <>
            {forgotSuccess ? (
              <>
                <p style={{ color: '#333', marginBottom: '1.5rem', fontSize: '1rem', lineHeight: 1.6 }}>
                  {message}
                </p>
                <button
                  type="button"
                  onClick={() => { setView('login'); setForgotSuccess(false); setMessage(null); }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: '#2ca9e1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {t('forgot.backToLogin')}
                </button>
              </>
            ) : (
              <>
                <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
                  {t('forgot.intro')}
                </p>
                <form onSubmit={handleForgotPassword}>
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: '500', color: '#333' }}>
                      {t('forgot.email')}
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('forgot.emailPlaceholder')}
                      className="form-input"
                      style={inputStyle}
                      autoComplete="email"
                    />
                  </div>
                  {message && (
                    <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>{message}</p>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: '#2ca9e1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: loading ? 'wait' : 'pointer'
                    }}
                  >
                    {loading ? t('forgot.submitting') : t('forgot.submit')}
                  </button>
                </form>
                <p style={{ marginTop: '1.25rem', fontSize: '0.9rem', color: '#666' }}>
                  <button
                    type="button"
                    onClick={() => { setView('login'); setMessage(null); }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#2ca9e1',
                      cursor: 'pointer',
                      padding: 0,
                      textDecoration: 'underline'
                    }}
                  >
                    {t('forgot.backToLogin')}
                  </button>
                </p>
              </>
            )}
          </>
        )}

        {view === 'reset' && (
          <>
            <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
              {t('reset.intro')}
            </p>
            <form onSubmit={handleResetPassword}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: '500', color: '#333' }}>
                  {t('reset.newPassword')}
                </label>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('reset.newPasswordPlaceholder')}
                  className="form-input"
                  style={inputStyle}
                  autoComplete="new-password"
                />
              </div>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: '500', color: '#333' }}>
                  {t('reset.confirmPassword')}
                </label>
                <PasswordInput
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('reset.confirmPasswordPlaceholder')}
                  className="form-input"
                  style={inputStyle}
                  autoComplete="new-password"
                />
              </div>
              {message && (
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>{message}</p>
              )}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: '#2ca9e1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: loading ? 'wait' : 'pointer'
                }}
              >
                {loading ? t('reset.submitting') : t('reset.submit')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// PaymentModal — customer flow to submit a partial payment proof
// ---------------------------------------------------------------------------

const PaymentModal = ({ order, user, session, onClose, onSuccess }) => {
  const { t } = useTranslation('account');
  // Treat the order prop as initial-state only; on open we re-fetch
  // /api/payments to capture any background admin actions (confirms, rejects,
  // other pending uploads) so the "te faltan" default and the client-side cap
  // line up with what the server will accept.
  const [total, setTotal] = useState(Number(order.totalAmount || 0));
  const [amountPaid, setAmountPaid] = useState(Number(order.amountPaid || 0));
  const [pendingTotal, setPendingTotal] = useState(0);
  const remaining = Math.max(0, total - amountPaid - pendingTotal);

  const [method, setMethod] = useState('bank_transfer');
  const [amount, setAmount] = useState(Math.max(0, Number(order.totalAmount || 0) - Number(order.amountPaid || 0)).toFixed(2));
  const [amountTouched, setAmountTouched] = useState(false);
  const [reference, setReference] = useState('');
  const [proof, setProof] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [bankInfo, setBankInfo] = useState(null);

  useEffect(() => {
    if (!session?.access_token) return undefined;
    let cancelled = false;
    fetch('/api/bank-info', {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled && data) setBankInfo(data.bank || data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [session?.access_token]);

  // Refresh totals + pending-upload sum from the server when the modal mounts
  // so the default amount and cap don't go stale relative to background
  // admin actions on the order list page.
  useEffect(() => {
    if (!session?.access_token || !order?.id) return undefined;
    let cancelled = false;
    fetch(`/api/payments?purchase_id=${encodeURIComponent(order.id)}`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data?.data) return;
        const freshTotal = Number(data.data.purchase?.totalAmount || 0);
        const freshPaid = Number(data.data.purchase?.amountPaid || 0);
        const pending = (data.data.payments || [])
          .filter((p) => p.status === 'pending_review')
          .reduce((s, p) => s + Number(p.amount || 0), 0);
        setTotal(freshTotal);
        setAmountPaid(freshPaid);
        setPendingTotal(pending);
        // Only reset the default if the customer hasn't started typing — once
        // they edit the field we respect their input even if the server cap
        // says less.
        if (!amountTouched) {
          setAmount(Math.max(0, freshTotal - freshPaid - pending).toFixed(2));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access_token, order?.id]);

  const formatPrice = (price, currency = order.currency || 'MXN') =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: String(currency).toUpperCase() }).format(price);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const parsed = parseFloat(amount);
    if (!parsed || isNaN(parsed) || parsed <= 0) {
      setError('Ingresa un monto válido mayor a 0.');
      return;
    }
    if (parsed > remaining + 0.01) {
      setError(`El monto no puede superar los ${formatPrice(remaining)} restantes.`);
      return;
    }
    if (!proof?.path) {
      setError('Sube el comprobante de pago antes de enviar.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/payments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          purchase_id: order.id,
          amount: parsed,
          method,
          reference: reference.trim() || null,
          proof_url: proof.path,
          proof_filename: proof.filename || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'No pudimos registrar el pago. Intenta de nuevo.');
        // The file is already in the private bucket — no Payments row will
        // ever reference it now, so it's orphaned. Best-effort cleanup; if
        // delete fails (network, RLS), the file stays but the user can
        // re-upload and retry.
        supabase.storage.from('payment_proofs').remove([proof.path])
          .catch((cleanupErr) => console.warn('[PaymentModal] proof cleanup failed:', cleanupErr?.message));
        return;
      }
      onSuccess?.();
    } catch (err) {
      setError(err.message || 'Error inesperado al enviar el pago.');
      // Same cleanup on a thrown network error.
      supabase.storage.from('payment_proofs').remove([proof.path])
        .catch((cleanupErr) => console.warn('[PaymentModal] proof cleanup failed:', cleanupErr?.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '480px',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '1.5rem',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontFamily: "'Gambado Sans', sans-serif" }}>{t('payment.title')}</h3>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666', lineHeight: 1 }}
            aria-label={t('payment.closeAriaLabel')}
          >×</button>
        </div>

        <p style={{ margin: '0 0 0.5rem', color: '#666', fontSize: '0.9rem' }}>
          {/* Match the ref formula in the order list (line ~371) so the
              customer sees the same #ABCD1234 number everywhere. */}
          Pedido #{(order.paymentIntentId || order.id || '').toString().slice(-8)} — te faltan <strong>{formatPrice(remaining)}</strong>.
        </p>

        <form onSubmit={handleSubmit}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginTop: '1rem', marginBottom: '0.35rem' }}>
            {t('payment.methodLabel')}
          </label>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            style={{ ...inputStyle, padding: '0.65rem' }}
          >
            <option value="bank_transfer">{t('payment.methods.bank_transfer')}</option>
            <option value="mercado_pago">{t('payment.methods.mercado_pago')}</option>
            <option value="other">{t('payment.methods.other')}</option>
          </select>

          {method === 'bank_transfer' && (
            <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px', fontSize: '0.85rem', lineHeight: 1.5 }}>
              <strong>{t('payment.bankDetails')}</strong>
              {bankInfo ? (
                <>
                  {bankInfo.bankName && <div>{t('payment.bankName')}: {bankInfo.bankName}</div>}
                  {bankInfo.beneficiary && <div>{t('payment.beneficiary')}: {bankInfo.beneficiary}</div>}
                  {bankInfo.clabe && <div>{t('payment.clabe')}: <code>{bankInfo.clabe}</code></div>}
                </>
              ) : (
                <div style={{ color: '#888' }}>{t('payment.bankContactAdmin')}</div>
              )}
            </div>
          )}

          {method === 'mercado_pago' && order.mercadoPagoUrl && (
            <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px', fontSize: '0.85rem' }}>
              <a
                href={order.mercadoPagoUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#2ca9e1', fontWeight: '600' }}
              >
                {t('payment.mercadoPagoLink')}
              </a>
              <p style={{ margin: '0.5rem 0 0', color: '#666' }}>
                {t('payment.mercadoPagoInstructions')}
              </p>
            </div>
          )}

          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginTop: '1rem', marginBottom: '0.35rem' }}>
            {t('payment.amountLabel')}
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => { setAmountTouched(true); setAmount(e.target.value); }}
            style={inputStyle}
          />

          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginTop: '1rem', marginBottom: '0.35rem' }}>
            {t('payment.referenceLabel')}
          </label>
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder={t('payment.referencePlaceholder')}
            style={inputStyle}
          />

          <div style={{ marginTop: '1rem' }}>
            <PaymentProofUploadField
              userId={user?.id}
              purchaseId={order.id}
              value={proof}
              onChange={setProof}
              onUploadingChange={setUploading}
            />
          </div>

          {error && (
            <p style={{ color: '#dc3545', fontSize: '0.9rem', margin: '0.5rem 0' }}>{error}</p>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: '#f0f0f0',
                color: '#333',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              {t('payment.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting || uploading || !proof?.path}
              style={{
                flex: 2,
                padding: '0.75rem',
                background: submitting || uploading || !proof?.path ? '#9bd0ec' : '#2ca9e1',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: submitting ? 'wait' : 'pointer',
              }}
            >
              {submitting ? t('payment.submitting') : t('payment.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountPage;
